import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t, getal, wanneer, geleden } from '../i18n.js';
import { St } from '../components/Status.jsx';
import Balken from '../components/Balken.jsx';
import Stapelstaven from '../components/Stapelstaven.jsx';

/**
 * De pagina Endpoints (docs/besluiten/endpoints-pagina.md): wat Cisco Secure Endpoint weet over de computers van een klant,
 * in drie delen: de computers, de detecties door de tijd, en de kwetsbare software. De grafieken bovenaan, de lijst van alle
 * computers daaronder. Voor medewerkers (de gekozen klant),
 * klanten (hun eigen) en bij meekijken: `haal(dagen)` bepaalt waar het vandaan komt.
 *
 * Kleuren uit het systeem. De detecties zijn standen en dragen hun statuskleur met een woord in de legenda en tooltip:
 * opgeruimd in ADTS-cyaan, indicator of compromise in hoog (oranje), niet opgeruimd in kritiek (rood).
 */
const REEKSEN = () => [
  { sleutel: 'opgeruimd', label: t('opgeruimd'), kleur: 'var(--cyan-ink)' },
  { sleutel: 'ioc', label: t('indicator of compromise'), kleur: 'var(--st-serious)' },
  { sleutel: 'niet_opgeruimd', label: t('niet opgeruimd'), kleur: 'var(--st-critical)' },
];

export default function Endpoints({ haal, sleutel, sub }) {
  const [dagen, zetDagen] = useState(30);
  const [alle, zetAlle] = useState(false);
  const q = useQuery({ queryKey: [...sleutel, 'endpoints', dagen], queryFn: () => haal(dagen), refetchInterval: 60_000 });
  const kop = (
    <div className="page-head"><div>
      <h1>Secure Endpoint</h1>
      <p className="sub">{sub ? `${sub} · ` : ''}{t('Wat Cisco Secure Endpoint weet over de computers')}</p>
    </div></div>
  );
  const periodes = [[7, t('7 dagen')], [30, t('30 dagen')], [90, t('90 dagen')]];
  const filters = (
    <div className="filters">
      {periodes.map(([d, l]) => <button key={d} type="button" className="chip" aria-pressed={dagen === d} onClick={() => zetDagen(d)}>{l}</button>)}
    </div>
  );
  if (q.isPending) return <>{kop}{filters}<p className="muted">{t('Laden…')}</p></>;
  if (q.isError) return <>{kop}{filters}<div className="melder rood">{t(q.error.message)}</div></>;
  const { computers: c, detecties: d, kwetsbaar } = q.data.data;
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{getal(n)}</div><div className="t">{label}</div></div>;
  const reeksen = REEKSEN();
  const lijst = alle ? c.lijst : c.lijst.slice(0, 25);

  if (!c.totals.totaal && !d.totals.n && !kwetsbaar.length) {
    return <>{kop}<section className="card"><p className="empty">{t('Nog niets uit Secure Endpoint. Koppel de organisatie onder Beheer › Secure Endpoint en zet de module aan; ADTS ZERO haalt elke vijf minuten op.')}</p></section></>;
  }

  return (
    <>
      {kop}{filters}

      <h2 className="sectiekop">{t('Computers')}</h2>
      <div className="tiles">
        {tegel(c.totals.totaal, t('computers'))}
        {tegel(c.totals.actief, t('ingecheckt in 24 uur'))}
        {tegel(c.totals.stil, t('al 7 dagen niet ingecheckt'), c.totals.stil ? 'warn' : '')}
        {tegel(c.totals.geisoleerd + c.totals.gecompromitteerd, t('geïsoleerd of gecompromitteerd'), c.totals.gecompromitteerd ? 'crit' : c.totals.geisoleerd ? 'warn' : '')}
      </div>
      <div className="grid-2" style={{ marginBottom: 18 }}>
        <section className="card"><header><h2>{t('Besturingssystemen')}</h2></header><Balken rijen={c.perOs} /></section>
        <section className="card"><header><h2>{t('Versies van de connector')}</h2></header><Balken rijen={c.connectors} /></section>
        <section className="card"><header><h2>{t('Groepen')}</h2></header><Balken rijen={c.perGroep} /></section>
        <section className="card"><header><h2>{t('Policies')}</h2></header><Balken rijen={c.perPolicy} /></section>
      </div>

      <h2 className="sectiekop">{t('Detecties')}</h2>
      <div className="tiles">
        {tegel(d.totals.n, t('detecties'))}
        {tegel(d.totals.niet_opgeruimd, t('niet opgeruimd'), d.totals.niet_opgeruimd ? 'crit' : '')}
        {tegel(d.totals.ioc, t('indicators of compromise'), d.totals.ioc ? 'warn' : '')}
        {tegel(d.totals.computers, t('computers met een detectie'))}
      </div>
      {!d.totals.n ? <section className="card" style={{ marginBottom: 18 }}><p className="empty">{t('Geen detecties in deze periode.')}</p></section> : (
        <>
          <section className="card" style={{ marginBottom: 18 }}>
            <header><h2>{t('Per dag')}</h2>
              <span className="meta legenda">{reeksen.map((r) => <span key={r.sleutel}><i style={{ background: r.kleur }} />{r.label}</span>)}</span></header>
            <Stapelstaven punten={d.perDag} reeksen={reeksen} label={t('Detecties per dag')} />
          </section>
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <section className="card"><header><h2>{t('Bedreigingen')}</h2></header>
              <Balken rijen={d.bedreigingen.map((x) => ({ naam: x.naam, n: x.n, extra: x.niet_opgeruimd ? t('{n} niet opgeruimd', { n: getal(x.niet_opgeruimd) }) : null }))} /></section>
            <section className="card"><header><h2>{t('Computers met de meeste detecties')}</h2></header><Balken rijen={d.computers} /></section>
            <section className="card"><header><h2>{t('Bestanden')}</h2></header><Balken rijen={d.bestanden} /></section>
          </div>
          <section className="card" style={{ marginBottom: 18 }}>
            <header><h2>{t('Laatste detecties')}</h2></header>
            <table>
              <thead><tr><th>{t('Wanneer')}</th><th>{t('Computer')}</th><th>{t('Wat')}</th><th className="hide-sm">{t('Bestand')}</th></tr></thead>
              <tbody>{d.laatste.map((x, i) => (
                <tr key={i}>
                  <td className="mono muted">{wanneer(x.at)}</td>
                  <td className="mono">{x.computer}{x.user_name && <><br /><span className="muted" style={{ fontSize: 12 }}>{x.user_name}</span></>}</td>
                  <td><St soort={x.soort === 'niet-opgeruimd' ? 'critical' : x.soort === 'ioc' ? 'serious' : 'neutral'}>{x.event_type}</St>
                    {x.detection && <><br /><span className="muted">{x.detection}</span></>}</td>
                  <td className="hide-sm mono" style={{ overflowWrap: 'anywhere', fontSize: 12 }}>{x.file_name ?? '—'}{x.file_path && <><br /><span className="muted">{x.file_path}</span></>}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>
        </>
      )}

      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Alle computers')}</h2><span className="meta">{t('eerst wat aandacht vraagt')}</span></header>
        <table>
          <thead><tr><th>{t('Computer')}</th><th className="hide-sm">{t('Besturingssysteem')}</th><th className="hide-sm">{t('Connector')}</th>
            <th className="hide-sm">{t('Groep en policy')}</th><th>{t('Laatst ingecheckt')}</th><th>{t('Stand')}</th></tr></thead>
          <tbody>{lijst.map((x, i) => (
            <tr key={i}>
              <td><b className="mono">{x.hostname ?? '—'}</b><br /><span className="muted mono" style={{ fontSize: 12 }}>{[...(x.internal_ips ?? []).slice(0, 2), x.external_ip].filter(Boolean).join(' · ')}</span></td>
              <td className="hide-sm">{x.os ?? '—'}</td>
              <td className="hide-sm mono">{x.connector ?? '—'}</td>
              <td className="hide-sm">{x.grp ?? '—'}<br /><span className="muted">{x.policy ?? ''}</span></td>
              <td>{x.last_seen ? <>{geleden(x.last_seen)}<br /><span className="muted" style={{ fontSize: 12 }}>{wanneer(x.last_seen)}</span></> : '—'}</td>
              <td>{x.compromised ? <St soort="critical">{t('gecompromitteerd')}</St>
                : x.isolated ? <St soort="serious">{t('geïsoleerd')}</St>
                : x.faults ? <St soort="warning">{t('{n} fout(en)', { n: x.faults })}</St>
                : x.last_seen && Date.now() - new Date(x.last_seen) > 7 * 86_400_000 ? <St soort="warning">{t('stil')}</St>
                : <St soort="good">{t('in orde')}</St>}</td>
            </tr>
          ))}</tbody>
        </table>
        {c.lijst.length > 25 && <div className="actions"><button type="button" className="btn" onClick={() => zetAlle((v) => !v)}>
          {alle ? t('Minder tonen') : t('Alle {n} tonen', { n: getal(c.lijst.length) })}</button></div>}
      </section>

      <h2 className="sectiekop">{t('Kwetsbare software')}</h2>
      <section className="card">
        {kwetsbaar.length ? (
          <table>
            <thead><tr><th>{t('Software')}</th><th style={{ textAlign: 'right' }}>{t('Computers')}</th><th>{t('Kwetsbaarheden')}</th></tr></thead>
            <tbody>{kwetsbaar.map((k) => (
              <tr key={`${k.app}-${k.version}`}>
                <td><b>{k.app}</b> <span className="mono muted">{k.version}</span><br /><span className="muted mono" style={{ fontSize: 12 }}>{k.hosts.join(', ')}</span></td>
                <td className="num" style={{ textAlign: 'right' }}>{getal(k.computers)}</td>
                <td><div className="cvelijst">{k.cves.slice(0, 12).map((x) => (
                  <a key={x.id} href={`https://nvd.nist.gov/vuln/detail/${x.id}`} target="_blank" rel="noreferrer" title={x.score ? `CVSS ${x.score}` : undefined}>
                    <St soort={x.kev ? 'critical' : (x.score ?? 0) >= 7 ? 'serious' : 'warning'}>{x.id}{x.kev ? ' · KEV' : ''}</St></a>))}
                  {k.cves.length > 12 && <span className="muted">+{k.cves.length - 12}</span>}</div></td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p className="empty">{t('Secure Endpoint meldde geen kwetsbare software (de laatste 90 dagen).')}</p>}
        <p className="noot">{t('Uit de melding Vulnerable Software Detected van Secure Endpoint, naast CISA KEV. Rood: actief misbruikt; werk die software eerst bij.')}</p>
      </section>
    </>
  );
}
